import { CircularProgress, Typography, useMediaQuery } from "@material-ui/core"
import { useTheme } from "@material-ui/styles"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useState } from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import InfiniteScroll from "react-infinite-scroll-component"
import { updateCategoryOrder } from "~/actions/categories"
import ListTitle from "~/components/Common/ListTitle"
import NoData from "~/components/Common/NoData"
import AppContext from "~/contexts/App"
import Box from "../../Common/Box"
import CategoryCard from "../CategoryCard"
import useStyles from "./styles"

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

function CategoryItem({ category, index, searchValue }) {
  const theme = useTheme()
  const phoneView = useMediaQuery(theme.breakpoints.down("sm"))
  return (
    <Draggable
      draggableId={category?.id?.toString()}
      index={index}
      isDragDisabled={searchValue?.length > 0 || phoneView}
    >
      {(provided) => {
        return (
          <CategoryCard
            key={index}
            provided={provided}
            category={category}
            index={index}
            locked={searchValue?.length > 0}
          />
        )
      }}
    </Draggable>
  )
}

CategoryItem.propTypes = {
  category: PropTypes.object,
  index: PropTypes.number,
  searchValue: PropTypes.string,
}

const CategoryCardList = ({
  categories,
  setOrderedCategories,
  searchValue,
  openCreateDialog,
}) => {
  const classes = useStyles()
  const { setPageSubTitle, openSnackBar } = useContext(AppContext)
  const [filteredData, setFilteredData] = useState([])
  const [showedData, setShowedData] = useState([])

  // set categories data to filteredData on first load
  useEffect(() => {
    if (categories?.length > 0 && !filteredData) {
      setFilteredData(categories)
    }
  }, [categories])

  // handles search by category name
  useEffect(() => {
    if (categories) {
      let newArray = [...categories]
      if (searchValue.length > 0) {
        // if a search val is applied
        if (searchValue.length > 0) {
          newArray = newArray.filter((x) =>
            x.name.toLowerCase().includes(searchValue.toLowerCase()),
          )
        }
      } else if (searchValue === "") {
        // handles no search value
        newArray = categories
      }
      setFilteredData(newArray)
      //show 20 first elements of the newArray
      setShowedData(newArray?.slice(0, 40 || []))
      //set client subtitle
      setPageSubTitle(newArray?.length)
    }
  }, [searchValue, categories])

  //loadData using infinite scroll
  const loadData = () => {
    setShowedData(() => filteredData.slice(0, showedData.length + 20))
  }
  function onCategoriesDragEnd(result) {
    if (!result.destination) {
      return
    }
    if (result.destination.index === result.source.index) {
      return
    }

    const orderedCategories = reorder(
      categories,
      result.source.index,
      result.destination.index,
    )
    try {
      const categoriesWithOrder = orderedCategories.map((category, index) => ({
        ...category,
        order: index,
      }))
      updateCategoryOrder({ categories: categoriesWithOrder })
      openSnackBar({ message: "Update category order" })
    } catch (error) {
      console.error("error updating category order")
      openSnackBar(error, "There was a problem updating category order")
    }

    setOrderedCategories(orderedCategories)
  }

  return (
    <Box overflow="auto" className={classes.container} id="clientsListScroll">
      <Box marginLeft="16px" className={classes.dataWrapper}>
        {categories?.length > 0 ? (
          <Box paddingLeft="16px" paddingRight="96px">
            <ListTitle
              list={[
                { name: "ID", size: "10%" },
                { name: "Order", size: "10%" },
                { name: "Name", size: "30%" },
                { name: "Type", size: "20%" },
                { name: "Partner Status", size: "15%" },
                { name: "Published", size: "15%" },
              ]}
            />
          </Box>
        ) : null}
        <Box className={classes.cardBox} height="100%">
          {filteredData?.length > 0 ? (
            <InfiniteScroll
              style={{ overflow: "hidden" }}
              dataLength={showedData?.length || 0}
              scrollableTarget="clientsListScroll"
              next={loadData}
              loader={
                <Box display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              }
              hasMore={
                (filteredData?.length > 0 &&
                  filteredData?.length > showedData?.length) ||
                false
              }
              endMessage={
                <Box width="100%" textAlign="center">
                  {filteredData?.length === showedData?.length && (
                    <Typography className={classes.postsInfiniteText}>
                      End of Categories List
                    </Typography>
                  )}
                </Box>
              }
            >
              <DragDropContext onDragEnd={onCategoriesDragEnd}>
                <Droppable droppableId="course">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {showedData.map((category, index) => {
                        return (
                          <CategoryItem
                            key={category.id}
                            category={category}
                            index={index}
                            searchValue={searchValue}
                          />
                        )
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </InfiniteScroll>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="100%"
              minHeight="80vh"
            >
              <NoData
                text="You have not created any categories"
                buttonFunc={openCreateDialog}
                buttonText="create a category"
                searchValue={searchValue}
                length={categories?.length}
                searchLength={filteredData?.length}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

CategoryCardList.propTypes = {
  searchValue: PropTypes.string,
  openCreateDialog: PropTypes.func,
  categories: PropTypes.array,
  setOrderedCategories: PropTypes.func,
}

export default CategoryCardList
